/**
 * Centralized API Client
 * ======================
 * 
 * Axios instance pre-configured with:
 * - Base URL from environment
 * - Auto-attached auth token via interceptor
 * - 401 handling (redirect to login)
 * - Error toasts for non-silent failures
 * 
 * Usage:
 *   import api from '../lib/api';
 *   const res = await api.get('/populations');
 *   await api.post('/simulations', payload);
 */

import axios from 'axios';
import { toast } from 'sonner';

const api = axios.create({
  baseURL: '/api',
});

// ── Request interceptor: attach auth token ──
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('synpop_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor: global error handling ──
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      // Token expired or invalid – clear and redirect
      localStorage.removeItem('synpop_token');
      delete axios.defaults.headers.common['Authorization'];
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Suppress toast when:
    // - request was cancelled via CancelToken / AbortController
    // - caller passed _silent: true in config (graceful degradation)
    // - no response at all (network-level drop, hot reload, cancelled requests)
    const isSilent = error.config?._silent === true;
    const hasResponse = !!error.response;

    if (!axios.isCancel(error) && !isSilent && hasResponse) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Something went wrong';

      // Don't toast for 403 MFA_REQUIRED (handled by login flow)
      if (!(status === 403 && error.response?.data?.detail === 'MFA_REQUIRED')) {
        toast.error(message);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
